import React, { useMemo } from 'react';

import Service from '@/pages/dashboard/components/PageContainer/Service';
import type { IComponentServiceProps } from '@/pages/dashboard/components/PageContainer/type';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { formatFilterValues } from '@/pages/dashboard/utils/utils';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';

import type { IFormattedServiceProps, TBeforeRequest } from './_type';

/**
 * 调用业务 beforeRequest，传入 (格式化后的标准 params, 原始 data)
 *  * - formattedParams: 已含 filters/start_time/end_time/period/metrics/game_code
 * - rawData: 原始 data（含 filterVal、gameCode 等）
 */
const callBeforeRequest = (
  beforeRequest: TBeforeRequest,
  formattedParams: any,
  rawData: any,
): any => {
  if (!beforeRequest) return formattedParams;
  if (typeof beforeRequest === 'string') {
    return stringToFunction(beforeRequest)(formattedParams, rawData);
  }
  return beforeRequest(formattedParams, rawData);
};

/**
 * 统一格式化：只保证这 6 个字段输出稳定
 * - filters / start_time / end_time / period / metrics / game_code
 */
const buildStandardParams = (rawParams: any, data: any) => {
  const params = { ...(rawParams || {}) };
  const filterVal = data?.filterVal;
  
  // game_code
  if (params.game_code === undefined) {
    params.game_code = data?.gameCode;
  }

  // metrics
  if (params.metrics === undefined) {
    params.metrics = data?.metric ?? data?.metrics;
  }

  // filters + time range + period
  if (params.filters === undefined) {
    const { rangeTime, timeType, ...filters } = filterVal || {};
    params.filters = formatFilterValues(filters);
    params.start_time = params.start_time ?? rangeTime?.[0];
    params.end_time = params.end_time ?? rangeTime?.[1];
    if (params.period === undefined && timeType !== undefined) {
      params.period = PERIODTIMEOBJ[timeType];
    }
  } else {
    params.filters = formatFilterValues(params.filters);
  }

  return params;
};

const FormattedService: React.FC<IFormattedServiceProps> = props => {
  const {
    ServiceComponent: serviceComponentProp,
    beforeRequest,
    formatBeforeRequest,
    metrics: configMetrics,
    extralParams,
    dataKey = 'data',
    ...serviceProps
  } = props;

  const serviceComponent = serviceComponentProp ?? Service;

  const wrappedBeforeRequest = useMemo(() => {
    return (data: any) => {
      const dataWithMetrics = configMetrics
        ? { ...data, metrics: configMetrics }
        : data;
      const baseParams = buildStandardParams({}, dataWithMetrics);
      const userParams = callBeforeRequest(beforeRequest, baseParams, data);
      if (userParams === false) return false;
      const formattedParams = formatBeforeRequest
        ? formatBeforeRequest(userParams, data)
        : buildStandardParams(userParams, data);
      return extralParams ? { ...formattedParams, ...extralParams } : formattedParams;
    };
  }, [beforeRequest, extralParams, configMetrics, formatBeforeRequest]);

  return React.createElement(serviceComponent, {
    ...(serviceProps as IComponentServiceProps),
    dataKey,
    beforeRequest: wrappedBeforeRequest,
  });
};

export default FormattedService;
export type { IFormattedServiceProps, TBeforeRequest, TServiceComponent } from './_type';
